/**
 * Logo Slider
 *
 * Automatisches Scrollen des horizontalen Logo-Sliders (Bounce-Effekt).
 *
 * Features:
 * - Auto-scroll mit Richtungswechsel an den Enden (Bounce)
 * - Pause bei Hover, permanenter Stop bei Nutzer-Interaktion
 * - IntersectionObserver: Animation läuft nur im Viewport
 * - prefers-reduced-motion Support
 * - Keyboard-Navigation (ArrowLeft/ArrowRight)
 */

export function initLogoSlider(): void {
    const container = document.getElementById('logo-slider_scroll-container') as HTMLElement;
    if (!container) return;

    const links = container.querySelectorAll<HTMLAnchorElement>('a');

    let autoScrollActive = true;
    let isPaused = false;
    let isInViewport = false;
    let animationId: number | null = null;
    let scrollDirection = 1; // 1 = right, -1 = left
    const speed = 2; // pixels per frame

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        autoScrollActive = false;
    }

    function pauseAutoScroll(): void {
        if (!autoScrollActive || isPaused) return;
        isPaused = true;
        if (animationId !== null) {
            cancelAnimationFrame(animationId);
            animationId = null;
        }
    }

    function resumeAutoScroll(): void {
        if (!autoScrollActive || !isPaused) return;
        isPaused = false;
        autoScroll();
    }

    function stopAutoScroll(): void {
        if (!autoScrollActive) return;
        autoScrollActive = false;
        isPaused = false;
        if (animationId !== null) {
            cancelAnimationFrame(animationId);
            animationId = null;
        }
        container.classList.add('user-interacted');
    }

    function autoScroll(): void {
        if (!autoScrollActive || isPaused || !isInViewport) return;

        container.scrollLeft += speed * scrollDirection;

        const maxScroll = container.scrollWidth - container.clientWidth;
        if (container.scrollLeft >= maxScroll - 1) {
            scrollDirection = -1;
        } else if (container.scrollLeft <= 1) {
            scrollDirection = 1;
        }

        animationId = requestAnimationFrame(autoScroll);
    }

    container.addEventListener('mouseenter', pauseAutoScroll);
    container.addEventListener('mouseleave', resumeAutoScroll);

    container.addEventListener('wheel', stopAutoScroll, { once: true, passive: true });
    container.addEventListener('touchstart', stopAutoScroll, { once: true, passive: true });
    container.addEventListener('pointerdown', stopAutoScroll, { once: true });
    container.addEventListener('keydown', stopAutoScroll, { once: true });

    links.forEach(link => {
        link.addEventListener('focus', stopAutoScroll, { once: true });
    });

    container.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            container.scrollBy({ left: -200, behavior: 'smooth' });
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            container.scrollBy({ left: 200, behavior: 'smooth' });
        }
    });

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            isInViewport = entry.isIntersecting;
            if (entry.isIntersecting && autoScrollActive && !isPaused && animationId === null) {
                autoScroll();
            } else if (!entry.isIntersecting && animationId !== null) {
                cancelAnimationFrame(animationId);
                animationId = null;
            }
        });
    }, { threshold: 0.2 });

    observer.observe(container);
}
